import type { OrderHistory } from '../../stores/appOrderHistoryStore';
import { getProductQuantity, normalizeProductName, resolveProductIdentifier } from '../../utils/platformUtils';
import { sourcingProductKey, SourcingProductRef, SourcingRunProduct, SourcingRunResponse, SourcingUnit } from './sourcingRunTypes';

export interface SourcingPeriod { start: string; end: string }
export interface SourcingVolume { quantity: number; valid: boolean }
export interface SourcingComparison {
  calculable: boolean;
  approximate: boolean;
  quantity: number;
  currentCost: number | null;
  alternativeCost: number | null;
  savings: number | null;
  savingsPercent: number | null;
}

export function sourcingProductRef(platform: string, articleId: string, name: string, supplierName?: string): SourcingProductRef {
  const identifier = resolveProductIdentifier(platform, articleId, name);
  return { key: sourcingProductKey(platform, identifier ?? name, supplierName), platform, articleId, name, supplierName };
}

/** Sourcing-only policy: never infer a piece's weight or density. */
export function canCompareSourcingUnits(a: SourcingUnit | null, b: SourcingUnit | null): boolean {
  return !!a && !!b && (a === b || (a !== 'piece' && b !== 'piece'));
}

export function isSourcingSelf(original: SourcingRunProduct, option: SourcingRunProduct): boolean {
  return original.key === option.key || (
    original.platform.toLowerCase() === option.platform.toLowerCase() &&
    (original.platform.toLowerCase() !== 'any_invoice' || (original.supplierName ?? '').trim().toLowerCase() === (option.supplierName ?? '').trim().toLowerCase()) &&
    normalizeProductName(original.name) === normalizeProductName(option.name)
  );
}

export function sourcingDisplayUnitPrice(product: SourcingRunProduct, showExVat: boolean): number | null {
  const price = showExVat ? product.unitPrice : product.unitPriceWithTaxes;
  return price !== null && Number.isFinite(price) && price > 0 ? price : null;
}

export function chooseSourcingOptions(snapshot: SourcingRunResponse): Record<string, string> {
  const selected: Record<string, string> = {};
  for (const original of Object.values(snapshot.originals)) {
    const eligible = (snapshot.options[original.key] ?? []).filter(option => {
      const candidate = snapshot.catalog[option.productKey];
      return candidate && !isSourcingSelf(original, candidate) && option.matchScore >= 75 &&
        canCompareSourcingUnits(original.unit, candidate.unit) &&
        original.unitPrice !== null && Number.isFinite(original.unitPrice) && original.unitPrice > 0 && candidate.unitPrice !== null &&
        Number.isFinite(candidate.unitPrice) && candidate.unitPrice > 0 && candidate.unitPrice < original.unitPrice;
    }).sort((a, b) => b.matchScore - a.matchScore || snapshot.catalog[a.productKey].unitPrice! - snapshot.catalog[b.productKey].unitPrice! || a.productKey.localeCompare(b.productKey));
    selected[original.key] = eligible[0]?.productKey ?? original.key;
  }
  return selected;
}

/** All costs use the same unit as Q (kg/litre/individual piece). VAT is display-only. */
export function getSourcingComparison(original: SourcingRunProduct, selected: SourcingRunProduct, volume: SourcingVolume, showExVat = true): SourcingComparison {
  const currentPrice = sourcingDisplayUnitPrice(original, showExVat);
  const selectedPrice = sourcingDisplayUnitPrice(selected, showExVat);
  const calculable = volume.valid && volume.quantity >= 0 && Number.isFinite(volume.quantity) &&
    canCompareSourcingUnits(original.unit, selected.unit) && sourcingDisplayUnitPrice(original, true) !== null &&
    sourcingDisplayUnitPrice(selected, true) !== null && currentPrice !== null && selectedPrice !== null;
  const approximate = canCompareSourcingUnits(original.unit, selected.unit) && original.unit !== selected.unit;
  if (!calculable) return { calculable: false, approximate, quantity: volume.quantity, currentCost: null, alternativeCost: null, savings: null, savingsPercent: null };
  const currentCost = volume.quantity * currentPrice!;
  const alternativeCost = volume.quantity * selectedPrice!;
  const savings = currentCost - alternativeCost;
  return { calculable: true, approximate, quantity: volume.quantity, currentCost, alternativeCost, savings, savingsPercent: currentCost > 0 ? savings / currentCost * 100 : 0 };
}

/** Sum each signed invoice line with its own pack metadata; never use weighted average packs. */
export function getSourcingVolumes(orders: OrderHistory[], originals: Record<string, SourcingRunProduct>, restaurantIds: string[], period: SourcingPeriod): Record<string, SourcingVolume> {
  const result: Record<string, SourcingVolume> = Object.fromEntries(Object.keys(originals).map(key => [key, { quantity: 0, valid: true }]));
  const restaurants = new Set(restaurantIds);
  const from = new Date(period.start).getTime(), to = new Date(period.end).getTime();
  const seen = new Set<string>();
  for (const order of orders) {
    const date = new Date(order.orderDate).getTime();
    if (!order.restaurantId || !restaurants.has(order.restaurantId) || date < from || date > to || !Number.isFinite(date) || seen.has(order.id)) continue;
    seen.add(order.id);
    for (const line of order.productData ?? []) {
      const identifier = resolveProductIdentifier(order.platform, line.articleId, line.name);
      if (!identifier) continue;
      const key = sourcingProductKey(order.platform, identifier, order.unknownSupplierName);
      const original = originals[key];
      if (!original) continue;
      const info = line.quantityInfo;
      const rawUnit = (info?.stdUnit as string | undefined)?.toLowerCase();
      const unit: SourcingUnit | null = rawUnit === 'g' || rawUnit === 'kg' ? 'g' : rawUnit === 'ml' || rawUnit === 'l' || rawUnit === 'liter' ? 'ml' : rawUnit === 'p' || rawUnit === 'piece' || rawUnit === 'pcs' ? 'piece' : null;
      const scale = rawUnit === 'kg' || rawUnit === 'l' || rawUnit === 'liter' ? 1000 : 1;
      const pack = Number(info?.stdAmount) * Number(info?.stdMultiplier) * scale;
      const count = getProductQuantity(line, order.platform);
      // Different physical units in one product's history are invalid even though a g/ml swap is allowed.
      if (!unit || unit !== original.unit || !Number.isFinite(pack) || Number(info?.stdAmount) <= 0 || Number(info?.stdMultiplier) <= 0 || !Number.isFinite(line.rawQuantity) || !Number.isFinite(count) || !Number.isFinite(line.quantity) || !Number.isFinite(line.price)) {
        result[key].valid = false;
        continue;
      }
      const signedCount = line.price < 0 ? -Math.abs(count) : count;
      result[key].quantity += signedCount * pack / (unit === 'piece' ? 1 : 1000);
    }
  }
  for (const volume of Object.values(result)) if (!Number.isFinite(volume.quantity) || volume.quantity < 0) volume.valid = false;
  return result;
}

/** Display-only provenance from already loaded purchases, never a valuation fallback. */
export function sourcingHistoricalOrderDates(orders: OrderHistory[], originals: Record<string, SourcingRunProduct>): Record<string, string> {
  const dates: Record<string, string> = {};
  for (const order of orders) {
    const date = new Date(order.orderDate);
    if (!Number.isFinite(date.getTime())) continue;
    const iso = date.toISOString();
    for (const line of order.productData ?? []) {
      const identifier = resolveProductIdentifier(order.platform, line.articleId, line.name);
      if (!identifier) continue;
      const key = sourcingProductKey(order.platform, identifier, order.unknownSupplierName);
      if (originals[key] && (!dates[key] || iso > dates[key])) dates[key] = iso;
    }
  }
  return dates;
}
